import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import useCellColor from "components/uiComponents/Table/model/useCellColor";
import IndeterminateCheckbox from "components/uiComponents/IndeterminateCheckbox";

import { setCurrentTableCellAC } from "store/actions/table";
import LinkCell from "../Link/Link";

import { valueTypes } from "utils/builder/ResultClasses";
import { combineRefsIntoOne } from 'utils/helpers';
import { getBounding } from "utils/elementExt";

const getCellTextTemplate = (ceilValue, unit) => {
  if (ceilValue === null || ceilValue === undefined || ceilValue === 'null' || ceilValue === '') {
    return '';
  }
  
  const ceilParsedValue = typeof ceilValue === "number" ? ceilValue.toFixed(2) : ceilValue;

  return unit ? `${ceilParsedValue} ${unit}` : `${ceilParsedValue}`;
};

const ViewCell = (props) => {
  const {
    cellDisplayType,
    table,
    cell,
    row,
    tableData,
    children,
    setIsEditable,
    setNodeRef,
    setChoicesState,
    choicesState,
    isEditable,
    style,
    ...rest
  } = props;

  const { t } = useTranslation();

  const elementRef = useRef(null);

  const columnId = cell.column.id;
  const cellValue = cell.getValue();
  const rowData = row.original;
  const dispatch = useDispatch();

  const color = useCellColor({
    columnId,
    cellValue,
    rowData,
    tableData,
  });

  const currentCellDataInRow = rowData[columnId];
  const currentUnit = cell.column.columnDef.meta?.unit;
  const templateString = getCellTextTemplate(currentCellDataInRow, currentUnit);

  const handleCheckboxChange = (value) => {
    const columnId = cell.column.id;
    const rowIndex = row.index;

    dispatch(setCurrentTableCellAC(columnId, tableData[rowIndex]?.lineElements, value));
  };

  const [isShowTooltip, setIsShowTooltip] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const getCoordinates = (event, type) => {
    const rect = getBounding(event.target);

    if (!choicesState) {
      setChoicesState({
        x: rect.left,
        y: rect.top,
        columnId: cell.column.id,
        rowIndex: row.index,
        type: type,
        existingValues: cell.column.columnDef.meta?.existingValue,
      });
    }
  };

  const handleDoubleClick = () => {
    if (elementRef?.current) {
      const elementHeight = elementRef.current.offsetHeight;
      elementRef.current.style.height = `${elementHeight - 22}px`;

      setIsEditable(true);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const onBlurHandler = (currentValue) => {
    const isTextOrNumberField = 
      cellDisplayType === valueTypes.customText ||
      cellDisplayType === valueTypes.customNumber ||
      cellDisplayType === valueTypes.proText ||
      cellDisplayType === valueTypes.proNumber;
    
    if (!isTextOrNumberField && !currentValue) {
      setIsEditable(false);
      setIsFocused(false);
      return;
    }
    
    const normalizedValue = typeof currentValue === 'string' 
      ? (currentValue.trim() === '' ? null : currentValue.trim())
      : currentValue;
    
    handleSetValue({ currentValue: normalizedValue, cell, row });
    setIsEditable(false);
    setIsFocused(false);
  };

  const handleSetValue = ({ currentValue, cell, row }) => {
    const columnId = cell.column.id;
    const rowIndex = row.index;
    const newValue = currentValue;

    dispatch(setCurrentTableCellAC(columnId, props?.tableData[rowIndex]?.lineElements, newValue));
  };

  switch (cellDisplayType) {
    case valueTypes.customLink:
    case valueTypes.proLink:
      return (
        <LinkCell
          contenteditable={isEditable}
          setNodeRef={combineRefsIntoOne(elementRef, setNodeRef)}
          onDoubleClick={handleDoubleClick}
          onBlur={(e) => onBlurHandler(e.currentTarget.textContent)}
          rowIndex={row.index}
          link={currentCellDataInRow}
          style={{ ...style, color }}
          {...rest}
        />
      );
    case valueTypes.customBool:
    case valueTypes.proBool:
      return (
        <td
          {...rest}
          style={{ ...style, color, cursor: 'pointer' }}
        >
          <IndeterminateCheckbox
            checked={!!currentCellDataInRow}
            handleChange={(e) => handleCheckboxChange(e.target.checked)}
          />
        </td>
      );
    case valueTypes.customEnumSingle:
    case valueTypes.proEnumSingle:
      return (
        <td
          onMouseEnter={() => setIsShowTooltip(true)}
          onMouseLeave={() => setIsShowTooltip(false)}
          ref={setNodeRef}
          onClick={(e) => getCoordinates(e, valueTypes.customEnumSingle)}
          style={{ ...style, color, cursor: 'pointer' }}
          {...rest}
        >
          {(isShowTooltip && templateString.length < 2) && <div className="tooltip-hover">{t('select_option')}</div>}
          {templateString}
        </td>
      );
    case valueTypes.customEnumMulty:
    case valueTypes.proEnumMulty:
      return (
        <td
          {...rest}
          onMouseEnter={() => setIsShowTooltip(true)}
          onMouseLeave={() => setIsShowTooltip(false)}
          ref={setNodeRef}
          onClick={(e) => getCoordinates(e, valueTypes.customEnumMulty)}
          style={{ ...style, color, cursor: 'pointer' }}
        >
          {(isShowTooltip && templateString.length < 2) && <div className="tooltip-hover">{t('select_option')}</div>}
          {templateString}
        </td>
      );
    case valueTypes.customDate:
    case valueTypes.proDate:
      return (
        <td
          {...rest}
          onMouseEnter={() => setIsShowTooltip(true)}
          onMouseLeave={() => setIsShowTooltip(false)}
          ref={setNodeRef}
          onClick={() => setIsEditable(true)}
          style={{ ...style, color, cursor: 'pointer' }}
        >
          {(isShowTooltip && templateString.length < 2) && <div className="tooltip-hover">{t('input_date')}</div>}
          {!!templateString && templateString !== "null" ? moment(templateString).format('YYYY/MM/DD') : ''}
        </td>
      );
      case valueTypes.date:
      return (
        <td ref={setNodeRef} style={{ ...style, color }} {...rest}>
        {!!templateString && templateString !== "null" ? moment(templateString).format('YYYY/MM/DD') : ''}
      </td>
        
      );
    case valueTypes.customText:
    case valueTypes.customNumber:
    case valueTypes.proText:
    case valueTypes.proNumber:
      return (
        <td
          contenteditable="true"
          ref={combineRefsIntoOne(elementRef, setNodeRef)}
          onBlur={(e) => onBlurHandler(e.currentTarget.textContent)}
          onFocus={handleFocus}
          onClick={handleDoubleClick}
          onMouseEnter={() => setIsShowTooltip(true)}
          onMouseLeave={() => setIsShowTooltip(false)}
          style={{ ...style, color, position: 'relative', cursor: 'pointer' }}
          {...rest}
        >
          {(isShowTooltip && templateString.length < 2 && !isFocused) && <div className="tooltip-hover">{t('input_text')}</div>}
          {templateString}
        </td>
      );
    default:
      return (
        <td ref={setNodeRef} style={{ ...style, color }} {...rest}>
          {templateString}
        </td>
      );
  }
};

export default ViewCell;
